import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../shared/services/language.service';
import { SettingService } from '../../shared/services/setting.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../shared/services/auth.service';
import { LoaderComponent } from '../../components/loader/loader.component';
import {
  catchError,
  of,
  Subscription,
  switchMap,
  take,
  timer,
} from 'rxjs';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, TranslateModule, LoaderComponent],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent {
  loading = false;
  mobileNumber: string = '';
  email: string = '';
  yourName: string = '';
  nationalId: string = '';
  isChangePasswordActive: boolean = false; // default false
  confirmPassword: string = '';
  newPassword: string = '';
  currentPassword: string = '';

  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  isOtpSent = false;
  otp = '';
  isLoading = false;
  resendTimer = 60;
  isTimerActive = false;
  private timerSubscription?: Subscription;
  otpPhoneNumber: string = '';
  language: string = 'en';

  constructor(
    public languageService: LanguageService,
    private settingService: SettingService,
    private toastr: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    this.authService.userProfile$.subscribe((userProfile) => {
      const attributes = userProfile?.attributes as Record<
        string,
        string[] | undefined
      >;
      this.yourName = `${userProfile?.firstName ?? ''} ${
        userProfile?.lastName ?? ''
      }`.trim();
      this.nationalId = attributes?.['nationalId']?.[0] ?? '';
      this.email = userProfile?.email ?? '';
      this.mobileNumber = attributes?.['PhoneNumber']?.[0] ?? '';
    });

    this.languageService.currentLang$.subscribe((lang) => {
      this.language = lang;
    });
  }

  toggleChangePassword() {
    this.isChangePasswordActive = !this.isChangePasswordActive;
    if (!this.isChangePasswordActive) {
      this.resetForm();
    }
  }

  resetForm() {
    this.isOtpSent = false;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.otp = '';
    this.timerSubscription?.unsubscribe();
    this.isTimerActive = false;
    this.resendTimer = 60;
  }

  onSendOtp(form: NgForm) {
    if (form.invalid) return;
    if (this.newPassword !== this.confirmPassword) {
      this.toastr.error('New password and confirm password do not match');
      return;
    }

    this.isLoading = true;
    const passwordData = {
      currentPassword: this.currentPassword,
      newPassword: this.newPassword,
      confirmPassword: this.confirmPassword,
    };

    this.settingService
      .verifyCurrentPassword(passwordData)
      .pipe(
        switchMap((response: any) => {
          if (!response) {
            this.toastr.error('Current password is incorrect');
            this.isLoading = false;
            return of(null);
          }

          return this.authService.userProfile$.pipe(
            switchMap((userProfile) => {
              if (!userProfile) {
                this.toastr.error('User profile is not available');
                this.isLoading = false;
                return of(null);
              }

              const attributes = userProfile.attributes as Record<
                string,
                string[] | undefined
              >;
              this.otpPhoneNumber = attributes?.['PhoneNumber']?.[0] ?? '';
              if (!this.otpPhoneNumber) {
                this.toastr.error('No phone number found in user profile');
                this.isLoading = false;
                return of(null);
              }

              // Send OTP via SMS
              return this.authService.generateOtp({
                otpSentVia: 'SMS',
                phoneNumber: this.otpPhoneNumber,
                otpType: 'reset_password',
                language: this.language,
              });
            })
          );
        }),
        catchError((err) => {
          this.isLoading = false;

          if (err.status === 400) {
            this.toastr.error(err.error?.message || 'Invalid request');
          } else if (err.status === 500) {
            this.toastr.error(
              'A server error occurred. Please try again later.'
            );
          } else {
            this.toastr.error(
              'Service is temporarily unavailable. Please try again later.'
            );
          }
          return of(null);
        })
      )
      .subscribe((otpResponse) => {
        if (otpResponse) {
          this.toastr.success(
            'OTP has been sent to your registered mobile number'
          );
          this.startResendTimer(); // Start the resend timer
          this.isOtpSent = true; // Disable input fields
        }

        this.isLoading = false;
      });
  }

  onVerifyAndChange() {
    this.timerSubscription?.unsubscribe();
    this.isTimerActive = false;
    if (!this.otp || this.otp.length < 4) {
      alert('Please enter a valid OTP');
      return;
    }
    this.isLoading = true;

    this.authService
      .validateOtp({
        otpSentVia: 'SMS',
        phoneNumber: this.otpPhoneNumber,
        otp: this.otp,
      })
      .subscribe({
        next: (res) => {
          if (res.otpValid === true) {
            this.loading = true;
            const passwordData = {
              currentPassword: this.currentPassword,
              newPassword: this.newPassword,
              confirmPassword: this.confirmPassword,
            };

            this.settingService.changePassword(passwordData).subscribe({
              next: (response: any) => {
                if (response.success) {
                  this.toastr.success(response.message);
                  this.resetForm();
                  setTimeout(() => {
                    this.authService.logout();
                  }, 1000);
                } else {
                  this.toastr.error(
                    response.message || 'Failed to change password'
                  );
                }
                this.loading = false;
                this.toggleChangePassword();
              },
              error: (err) => {
                if (err.status === 400) {
                  this.toastr.error(err.error.message);
                } else if (err.status === 500) {
                  this.toastr.error(
                    'A server error occurred. Please try again later'
                  );
                } else {
                  this.toastr.error(
                    'Service is temporarily unavailable. Please try again later'
                  );
                }
                this.loading = false;
              },
            });
          } else {
            this.isLoading = false;
            this.toastr.error('Invalid OTP. Please try again.');
            return;
          }
        },
        error: (err) => {
          this.toastr.error('Invalid OTP. Please try again.');
          this.isLoading = false;
        },
      });
  }

  startResendTimer() {
    this.resendTimer = 60;
    this.isTimerActive = true;
    this.timerSubscription?.unsubscribe();

    // Start a new timer that ticks every second
    this.timerSubscription = timer(1000, 1000)
      .pipe(take(60))
      .subscribe((tick) => {
        this.resendTimer = 60 - (tick + 1);
        if (this.resendTimer === 0) {
          this.isTimerActive = false;
        }
      });
  }

  onResendOtp() {
    if (this.isTimerActive || this.isLoading) return;

    this.isLoading = true;

    this.authService
      .reGenerateOtp({
        otpSentVia: 'SMS',
        phoneNumber: this.otpPhoneNumber,
        otpType: 'reset_password',
        language: this.language,
      })
      .subscribe({
        next: () => {
          this.toastr.success('OTP sent successfully');
        },
        error: (err) => {
          this.toastr.error('Failed to send OTP. Please try again.');
        },
      });
    this.isLoading = false;
    this.startResendTimer();
  }

  togglePasswordVisibility(field: string) {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  onSaveProfile(form: any) {
    if (form.invalid) {
      this.toastr.error('Please fill out all required fields correctly');
      return;
    }
    this.loading = true;
    const nameParts = this.yourName.trim().split(' ');
    const profileData = {
      firstName: nameParts.shift() || '',
      lastName: nameParts.join(' '),
      nationalId: this.nationalId.trim(),
      email: this.email.trim(),
      phoneNumber: this.mobileNumber.trim(),
    };

    this.settingService.updateProfile(profileData).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.toastr.success(response.message);
          this.authService.loadUserProfileWithRetry();
        } else {
          this.toastr.error(response.message || 'Failed to update profile');
        }
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error('Failed to update profile');
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.timerSubscription?.unsubscribe();
  }
}
